import { dateProp, idProp, model, Model, modelAction, prop } from '@weavix/mobx';
import { Cache } from '@weavix/utils/src/cache';
import { User, userRef } from '../users-store/users-store';
import { channelRef } from './channels-store';
import { ChannelMessageData, MessageType } from '@weavix/models/src/channel/channel-message';
import { i18nLocale } from '@weavix/models/src/translate/translate';
import { isFeatureEnabled } from 'models-mobx/feature-flags-store/is-feature-enabled';
import { FeatureId } from '@weavix/models/src/features/feature';
import { speaksMyLanguage } from '../users-store/my-languages';

const senderCache = new Cache<User>({ duration: 60_000, jitter: 60_000 });

@model('ChannelMessage')
export class ChannelMessage extends Model({
    id: idProp,
    channelId: prop<string>(),
    data: prop<ChannelMessageData | undefined>(),
    date: dateProp(),
    message: prop<string>(),
    type: prop<MessageType | undefined>(),
    senderId: prop<string>(),
    pending: prop<boolean>(),
    error: prop<boolean>(),
    partial: prop<boolean>(),
    translations: prop<{ [locale in i18nLocale]?: string }>(),
    /** The reader's toggle; undefined until they toggle, when {@link ChannelMessage.defaultTranslated} decides. */
    translated: prop<boolean | undefined>(),
    uploaded: dateProp(),
    offline: prop<boolean>(),
    sequence: prop<number>(),
    fromTeams: prop<boolean>(),
    edited: dateProp(),
    /**
     * Read-only here. Web radio never writes it; the backend sets `'streaming'` when the first
     * speech chunk creates the bubble and `'complete'` when the clip settles. Senders on builds
     * that predate streaming leave it undefined, which reads as settled.
     */
    streamingState: prop<'streaming' | 'complete' | undefined>(),
}) {
    sameUserAsNext: boolean;
    sameUserAsPrevious: boolean;
    groupWithNext: boolean;
    groupWithLast: boolean;

    constructor(...args) {
        super(...args);
        this.senderId = args[0].sender?.id;
        if (document.location.hostname.includes('-canary.weavix')) {
            const fixUri = (uri: string) => {
                if (uri?.includes('-canary.weavix')) return uri;
                return uri?.replace('.weavix', '-canary.weavix');
            };
            this.data?.attachments?.forEach(attachment => {
                if (attachment.file?.uri) attachment.file.uri = fixUri(attachment.file.uri);
                if (attachment.thumbnailUri) attachment.thumbnailUri = fixUri(attachment.thumbnailUri);
            });
        }
    }

    get hasTranslation() {
        if (!this.translations) return false;
        const locale = localStorage.getItem('lang') || 'en';
        const found = Object.keys(this.translations).find(txLocale => {
            return txLocale.split('-')[0] === locale.split('-')[0];
        });
        return !!found;
    }

    get locale() {
        return this.data?.locale ?? null;
    }

    /** Shows the translation until toggled, except a message in one of the reader's languages shows its original under `BilingualSpeech`. */
    get defaultTranslated() {
        return this.translated ?? (this.hasTranslation && !(speaksMyLanguage(this.locale) && isFeatureEnabled(FeatureId.BilingualSpeech)));
    }

    @modelAction
    toggleTranslation(translated?: boolean) {
        this.translated = translated ?? !this.defaultTranslated;
    }

    /**
     * The message as this viewer should read it. Falls back to the source when the translation for
     * their locale is empty, which the backend writes as a placeholder while one is still in
     * flight - returning it verbatim rendered a blank bubble over a clip that did transcribe.
     */
    get translatedMessage() {
        if (!this.translations || !this.defaultTranslated) return this.message;
        const locale = localStorage.getItem('lang') || 'en';
        const found = Object.keys(this.translations).find(txLocale => {
            return txLocale.split('-')[0] === locale.split('-')[0];
        });
        return found && this.translations[found]?.trim() ? this.translations[found] : this.message;
    }

    get viewingTranslation() {
        return this.defaultTranslated && this.hasTranslation;
    }

    get sender() {
        if (!this.senderId) return null;
        const cached = senderCache.get(this.senderId);
        if (cached) return cached;

        const currentSender = userRef(this.senderId).maybeCurrent;
        if (currentSender) {
            return senderCache.set(this.senderId, currentSender);
        } else {
            return new User({
                firstName: 'Unknown',
                lastName: 'User',
                id: this.senderId,
                deleted: true,
            }, true);
        }
    }

    get channel() {
        return channelRef(this.channelId).maybeCurrent;
    }

    get isTeamsMessage() {
        return !!this.fromTeams;
    }

    @modelAction
    setError() {
        this.error = true;
    }
}
